import type { ReactNode } from 'react';

import { ReportFooter } from './report-footer';

import { cn } from '@/lib/utils';

export interface ReportPageProps {
    pageNumber: number;
    totalPages: number;
    children: ReactNode;
    className?: string;
}

/**
 * A4 page container for report preview.
 *
 * Renders at fixed A4 proportions (210mm x 297mm at 96dpi = 794px x 1123px).
 * Content fills the page body; the footer is pinned at the bottom.
 * This same DOM structure will be targeted by html2canvas in Phase 2.
 *
 * The height is fixed (not a minimum): ReportPreviewLayout reserves exactly
 * one A4 slot per page, so an overflowing page would bleed over the next one
 * with no visual separation. Overflowing content is clipped instead — pages
 * must size their content to fit (flexible bands, pagination, …).
 */
export function ReportPage({ pageNumber, totalPages, children, className }: ReportPageProps) {
    return (
        <div
            className={cn(
                'relative flex flex-col overflow-hidden bg-white shadow-lg',
                'text-[11px] text-gray-800',
                className
            )}
            data-report-page={pageNumber}
            style={{ width: '794px', height: '1123px', padding: '40px 48px' }}
        >
            {/*
             * min-h-0: without it the body's min-height:auto keeps it at content
             * height, pushing the footer and bottom margin off the fixed-A4 sheet
             * when intrinsically tall content (e.g. an aspect-ratio-preserving SVG)
             * exceeds the available space. overflow-hidden clips any residual
             * overflow above the footer instead of over the margin.
             */}
            <div className="flex flex-1 flex-col mb-2 min-h-0 overflow-hidden">{children}</div>
            <ReportFooter pageNumber={pageNumber} totalPages={totalPages} />
        </div>
    );
}
